"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { MemorialModel, MemorialParcela } from "./domain/models";

type MemorialByRoute = {
  routeId: string;
  memorial: MemorialModel;
  isSaved: boolean;
};

type MemorialStoreState = {
  memorialByRouteId: Record<string, MemorialByRoute>;
  hasHydrated: boolean;
};

type MemorialStoreActions = {
  setHasHydrated: (value: boolean) => void;
  getMemorialById: (routeId: string) => MemorialModel | null;
  isMemorialSavedById: (routeId: string) => boolean | null;
  setMemorialById: (
    routeId: string,
    memorial: MemorialModel,
    isSaved?: boolean,
  ) => void;
  setParcelasById: (
    routeId: string,
    parcelas: MemorialParcela[],
    isSaved?: boolean,
  ) => void;
  setSavedById: (routeId: string, isSaved: boolean) => void;
  clearById: (routeId: string) => void;
  reset: () => void;
};

type MemorialStore = MemorialStoreState & MemorialStoreActions;

const MEMORIAL_STORAGE_KEY = "geoplatform-memorial-storage";

const defaultState: MemorialStoreState = {
  memorialByRouteId: {},
  hasHydrated: false,
};

export const useMemorialStore = create<MemorialStore>()(
  persist(
    (set, get) => ({
      ...defaultState,

      setHasHydrated: (value) => set({ hasHydrated: value }),

      getMemorialById: (routeId) =>
        get().memorialByRouteId[routeId]?.memorial ?? null,

      isMemorialSavedById: (routeId) =>
        get().memorialByRouteId[routeId]?.isSaved ?? null,

      setMemorialById: (routeId, memorial, isSaved = false) =>
        set((state) => ({
          memorialByRouteId: {
            ...state.memorialByRouteId,
            [routeId]: {
              routeId,
              memorial,
              isSaved,
            },
          },
        })),

      setParcelasById: (routeId, parcelas, isSaved = false) =>
        set((state) => {
          const current = state.memorialByRouteId[routeId];
          if (!current) return state;

          return {
            memorialByRouteId: {
              ...state.memorialByRouteId,
              [routeId]: {
                ...current,
                memorial: {
                  ...current.memorial,
                  parcelas,
                },
                isSaved,
              },
            },
          };
        }),

      setSavedById: (routeId, isSaved) =>
        set((state) => {
          const current = state.memorialByRouteId[routeId];
          if (!current) return state;

          return {
            memorialByRouteId: {
              ...state.memorialByRouteId,
              [routeId]: {
                ...current,
                isSaved,
              },
            },
          };
        }),

      clearById: (routeId) =>
        set((state) => {
          const next = { ...state.memorialByRouteId };
          delete next[routeId];
          return { memorialByRouteId: next };
        }),

      reset: () => set({ ...defaultState }),
    }),
    {
      name: MEMORIAL_STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
